import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Modal, Form, Input, Typography, Popconfirm, message, Input as AntInput, Space, Select } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined, FilterOutlined } from '@ant-design/icons';
import type { TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import ApiService from '../service/api-service';
import { Product, ProductSortField, SortOrder } from '../types/product';

const { Title } = Typography;
const { Search } = AntInput;
const { Option } = Select;

const Products: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [searchText, setSearchText] = useState('');
  const [sortField, setSortField] = useState<ProductSortField>(ProductSortField.CREATED_AT);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DESC);

  const fetchProducts = async (
    page: number = pagination.current,
    limit: number = pagination.pageSize,
    search: string = searchText,
    sort: ProductSortField = sortField,
    order: SortOrder = sortOrder
  ) => {
    try {
      setLoading(true);
      const response = await ApiService.getProducts({
        page,
        limit,
        search,
        sortBy: sort,
        sortOrder: order
      });

      setProducts(response.data.products);
      setPagination({
        current: response.data.pagination.currentPage,
        pageSize: response.data.pagination.limit,
        total: response.data.pagination.total
      });
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _: any,
    sorter: SorterResult<Product> | SorterResult<Product>[]
  ) => {
    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const sort = (currentSorter?.field as ProductSortField) || ProductSortField.CREATED_AT;
    const order = currentSorter?.order === 'ascend' ? SortOrder.ASC : SortOrder.DESC;
    
    setSortField(sort);
    setSortOrder(order);
    fetchProducts(
      pagination.current,
      pagination.pageSize,
      searchText,
      sort,
      order
    );
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    fetchProducts(1, pagination.pageSize, value, sortField, sortOrder);
  };

  const columns = [
    {
      title: 'Product Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <span className="font-medium text-gray-800">{text}</span>
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => <span className="text-gray-600">{text}</span>
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => <span className="text-gray-500">{new Date(date).toLocaleDateString()}</span>
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Product) => (
        <div className="flex space-x-2">
          <Button
            type="text"
            icon={<EditOutlined />}
            className="text-cyan-600 hover:text-cyan-500"
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Delete Product"
            description="Are you sure you want to delete this product?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ className: 'bg-cyan-600 hover:bg-cyan-500' }}
          >
            <Button
              type="text"
              icon={<DeleteOutlined />}
              className="text-red-600 hover:text-red-500"
            />
          </Popconfirm>
        </div>
      )
    }
  ];

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    form.setFieldsValue(product);
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      setLoading(true);
      await ApiService.deleteProduct(id);
      messageApi.success('Product deleted successfully');
      // Refresh the products list
      await fetchProducts();
    } catch (error: any) {
      // Handle validation errors (e.g., product in use)
      messageApi.error(error.message || 'Failed to delete product');
    } finally {
      setLoading(false);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      try {
        if (editingProduct) {
          await ApiService.updateProduct(editingProduct.id, values);
          messageApi.success('Product updated successfully');
        } else {
          await ApiService.createProduct(values);
          messageApi.success('Product created successfully');
        }
        
        // Refresh the products list
        await fetchProducts();
        setIsModalVisible(false);
        form.resetFields();
        setEditingProduct(null);
      } catch (error: any) {
        // Handle validation errors and show them in the message
        messageApi.error(error.message || 'Failed to save product');
      }
    } catch (error) {
      // This error is from form validation
      console.error('Form validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingProduct(null);
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {contextHolder}
      <div className="flex justify-between items-center mb-8">
        <div>
          <Title level={4} className="text-gray-800 mb-2">Products</Title>
          <p className="text-gray-500">Manage your product catalog</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsModalVisible(true)}
          className="bg-cyan-600 hover:bg-cyan-500 !rounded-lg"
        >
          Add Product
        </Button>
      </div>

      <Card className="shadow-sm rounded-lg border-gray-100">
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <Input
            placeholder="Search products..."
            prefix={<SearchOutlined className="text-gray-400" />}
            className="max-w-xs !rounded-lg"
            allowClear
            onChange={(e) => handleSearch(e.target.value)}
          />
          <Space className="ml-auto">
            <span className="text-gray-500 flex items-center gap-2">
              <FilterOutlined /> Sort by:
            </span>
            <Select
              value={sortField}
              onChange={(value) => {
                setSortField(value);
                fetchProducts(
                  pagination.current,
                  pagination.pageSize,
                  searchText,
                  value,
                  sortOrder
                );
              }}
              className="w-40 !rounded-lg"
              popupClassName="rounded-lg"
            >
              <Option value={ProductSortField.CREATED_AT}>Created At</Option>
              <Option value={ProductSortField.NAME}>Name</Option>
            </Select>
            <Select
              value={sortOrder}
              onChange={(value) => {
                setSortOrder(value);
                fetchProducts(
                  pagination.current,
                  pagination.pageSize,
                  searchText,
                  sortField,
                  value
                );
              }}
              className="w-32 !rounded-lg"
              popupClassName="rounded-lg"
            >
              <Option value={SortOrder.ASC}>Ascending</Option>
              <Option value={SortOrder.DESC}>Descending</Option>
            </Select>
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={products}
          rowKey="id"
          pagination={pagination}
          onChange={handleTableChange}
          loading={loading}
          className="border-gray-100"
        />
      </Card>

      <Modal
        title={editingProduct ? "Edit Product" : "Add New Product"}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okButtonProps={{ className: 'bg-cyan-600 hover:bg-cyan-500' }}
        maskClosable={false}
      >
        <Form
          form={form}
          layout="vertical"
          className="mt-4"
        >
          <Form.Item
            name="name"
            label="Product Name"
            rules={[{ required: true, message: 'Please input the product name!' }]}
          >
            <Input placeholder="Enter product name" className="rounded-lg" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please input the product description!' }]}
          >
            <Input.TextArea
              placeholder="Enter product description"
              rows={4}
              className="rounded-lg"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Products; 